import { useUIDSeed } from "react-uid";
import styled from "@emotion/styled";
import PropTypes from "prop-types";

import { Media } from "@hmn/coolinarika-web-core/controllers/MediaContext";
import { capitalizeFirstLetter } from "@hmn/coolinarika-web-core/helpers";

import { ConditionalWrapper } from "../../ConditionalWrapper";
import { HeadingSection, headingSectionSizes } from "../../Heading/components/Section";
import { ForkSpoonIcon, Icon } from "../../Icon";
import { Link } from "../../Link";
import { Separator } from "../../Separator";
import { TagPill, TagPillsList } from "../../Tag/components";
import styles from "./Tags.style";

const TagsStyled = styled.section((props) => ({ ...styles(props) }));

const Tags = ({
    className,
    titleTagsLinkId,
    titleTagsLinkSlug,
    title,
    titleTagComponent: TitleTag,
    tags,
    tagBasePath,
    hasShowMoreDishes,
    onShowMoreDishesClick,
    showMoreDishesButtonTitle,
    showWave,
    ...rest
}) => {
    const uid = useUIDSeed();

    return (
        <TagsStyled className={className} {...rest}>
            <div className="tagsSectionInner">
                {title && (
                    <div className="tagsSectionHead">
                        <HeadingSection
                            size={headingSectionSizes[TitleTag === "h1" ? "LARGE" : "HEADING"]}
                            TagComponent={TitleTag}
                            className="tagsSectionTitle"
                        >
                            <ConditionalWrapper
                                condition={!!titleTagsLinkId}
                                href={`/${tagBasePath}/${titleTagsLinkSlug}`}
                                title={`Pogledaj sve ${title.toLocaleLowerCase()}`}
                                Wrapper={Link}
                            >
                                {capitalizeFirstLetter(title)}
                            </ConditionalWrapper>
                        </HeadingSection>
                        {showWave && (
                            <Media lessThan="lg">
                                <Separator
                                    className="tagsSectionWave"
                                    componentRight={
                                        <Icon icon={ForkSpoonIcon} viewBox="0 0 20 16" />
                                    }
                                />
                            </Media>
                        )}
                    </div>
                )}

                <TagPillsList
                    itemsSpacing={10}
                    variant="secondary"
                    hasAddTagButton={hasShowMoreDishes}
                    onAddTagButtonClick={onShowMoreDishesClick}
                    addTagTitle={showMoreDishesButtonTitle}
                    limit={-1} // @INFO: Hack to always show the "Show more" button
                    isDishTags
                    className="tagsSectionContent"
                >
                    {!!tags?.length &&
                        tags?.map((item, index) => (
                            <TagPill
                                key={uid(item?.id || index)}
                                href={`/${tagBasePath}/${item?.parent?.slug}/${item?.slug}/`}
                                name={item?.title}
                                label={item?.title}
                                // hasSpacing
                                isRounded
                                isDishTag
                            />
                        ))}
                </TagPillsList>

                {showWave && (
                    <Media greaterThan="md">
                        <Separator
                            className="tagsSectionWave"
                            componentRight={<Icon icon={ForkSpoonIcon} viewBox="0 0 20 16" />}
                        />
                    </Media>
                )}
            </div>
        </TagsStyled>
    );
};

Tags.propTypes = {
    className: PropTypes.string,
    titleTagsLinkId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    titleTagsLinkSlug: PropTypes.string,
    title: PropTypes.string,
    titleTagComponent: PropTypes.elementType,
    tags: PropTypes.oneOfType([() => null, PropTypes.object]),
    tagBasePath: PropTypes.string,
    hasShowMoreDishes: PropTypes.bool,
    onShowMoreDishesClick: PropTypes.func,
    showMoreDishesButtonTitle: PropTypes.string,
    showWave: PropTypes.bool,
};

Tags.defaultProps = {
    className: undefined,
    titleTagsLinkId: undefined,
    titleTagsLinkSlug: undefined,
    title: undefined,
    titleTagComponent: "p",
    tags: undefined,
    tagBasePath: "tags",
    hasShowMoreDishes: true,
    onShowMoreDishesClick: () => {},
    showMoreDishesButtonTitle: "Pogledaj sve",
    showWave: false,
};

export default Tags;
